import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from "react-router-dom";

import Grid from '@mui/material/Grid2';
import { 
  ModelBase,
} from "@models";
import { 
  OptionalFeatureBase,
  FeatureBase,  
  FeatureSource,
} from "@models";
import { 
  DandDFeatureSource,
  CharacterLimitation,
  TemplateBase,
  TableBase,
  Table
} from "@dndModels";
import { 
  DATA_TYPE_INFO,
  FEATURE_SOURCE_TYPE_TO_NAME_MAP 
} from "@dndModels/Constants";
// import { ColorsType } from '../../../DandDPalette';

import DandDAPI from "@dndUtilities/system_api";

import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import CharacterLimitationBox from "@dndComponents/limitations/CharacterLimitationBox";

import ModelBaseDetails from "@dndComponents/ModelBaseDetails";

import FeatureBasesInput from "@dndComponents/feature/FeatureBasesInput";
import FeatureBaseInput from "@dndComponents/feature/FeatureBaseInput";
import OptionalFeatureBasesInput from "@dndComponents/feature/OptionalFeatureBases";
import OptionalFeatureBaseInput from "@dndComponents/feature/OptionalFeatureBaseInput";

import CommonUtilities from "@utilities/common_utilities";
import LoadingBox from "@components/input/LoadingBox";
import TemplateBox from "@dndComponents/TemplateBox";
import StringsBox from "@components/input/strings/StringsBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import ButtonBox from "@components/input/buttons/ButtonBox";
import TableInput from "@dndComponents/other/TableInput";
import ArrowBackButton from "@components/input/buttons/icon_buttons/ArrowBackButton";
// import ColorsBox from "@components/input/buttons/ColorsBox";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import { DataType } from '@dndModels/Types';
import DisplayStringWithReplacementsRecursive from './display/DisplayStringWithReplacementsRecursive';

interface Props {
  obj: DandDFeatureSource;
  data_type: DataType;
  change_time: number;
  image_options?: string[];
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  extra_menu_options?: { [menu_option: string]: React.ReactNode };
  disabled?: boolean;
  onChange: (changed: DandDFeatureSource) => void;
  onTabChange?: ((changed: string) => void) | null;
  onSubmit?: (changed: ModelBase | DandDFeatureSource) => void;
  onSubmitted?: (changed: ModelBase | DandDFeatureSource) => void;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function FeatureSourceInput({ 
  obj, 
  data_type, 
  change_time, 
  header,
  children,
  footer, 
  extra_menu_options = {},
  onChange, 
  onTabChange = null,
  image_options,
  onSubmit, 
  onSubmitted
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finished_loading, setFinishedLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [myObj, setObj] = useState(obj.clone());
  const [tab, setTab] = useState("Description");
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | OptionalFeatureBase | null>(null);
  const [optional_feature_bases, setOptionalFeatureBases] = useState<OptionalFeatureBase[]>([]);
  const [my_change_time, setChangeTime] = useState(change_time);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [level_filter, setLevelFilter] = useState(-1);
  const [table_id, setTableID] = useState("");
  const [topHeight, setTopHeight] = useState(0);
    
  const heightFinder: any = React.createRef();

  const updateHeight = useCallback(() => {
    if (heightFinder.current instanceof HTMLElement) {
      // console.log(heightFinder.current.clientHeight);
      setTopHeight(heightFinder.current.clientHeight);
      setReloading(true);
    }
  }, [heightFinder]);

  const load = useCallback( async () => {
    setLoading(true);
    // debugger;
    const res = await api.getObjects({ data_type: "optional_feature_base" });
    if (res) {
      const new_ofbs = res as OptionalFeatureBase[];
      // console.log(new_ofbs);
      // console.log(new_ofbs.filter(o => o.feature_source_type === obj.feature_source_type && o.feature_source_id === obj._id));
      setOptionalFeatureBases(new_ofbs.filter(o => o.feature_source_id === obj._id));
    }
    setLoading(false);
  }, [obj]);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);
  
  useEffect(() => {
    if (fetched && !loading && !finished_loading) {
      setFinishedLoading(true);
      updateHeight();
    }
    // Update height on window resize
    window.addEventListener('resize', updateHeight);

    // Clean up the event listener
    return () => window.removeEventListener('resize', updateHeight);
  }, [fetched, loading, finished_loading, updateHeight]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const disabled = api.user === null || !api.online || 
    (!api.user.admin && 
      myObj._id !== "" && 
      myObj.owner_id !== api.user._id);
    
  useEffect(() => {
    if (change_time > my_change_time) {
      setChangeTime(change_time);
      setObj(obj.clone());
    }
  }, [change_time, my_change_time, obj]);

  // const renderColors = () => {
  //   return (
  //     <Grid
  //       key="colors"
  //       size={{
  //         xs: 12
  //       }}>
  //       <ColorsBox
  //         colors={obj.colors}
  //         onChange={(changed: ColorsType) => {
  //           const new_obj = obj;
  //           new_obj.colors = changed;
  //           setObj(new_obj);
  //           setChangeTime(common_util.get_time_number());
  //         }}
  //       />
  //     </Grid>
  //   );
  // }

  const renderTab = () => {
    const height = window.innerHeight - api.topHeight - topHeight - 45;
    let content: any = null;
    if (tab === "Description") {
      // console.log(onSubmitted);
      content = (
        <>
          <Grid size={12}>
            <ModelBaseDetails
              obj={myObj}
              disabled={disabled}
              change_time={my_change_time}
              image_options={image_options}
              topHeight={topHeight}
              no_scrollbar
              onChange={(changed: ModelBase) => {
                // debugger;
                if (changed instanceof FeatureSource) {
                  const new_obj = myObj.clone();
                  new_obj.copy(changed);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  onChange(new_obj);
                }
              }}
              onSubmit={onSubmit}
              onSubmitted={onSubmitted}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { disabled ? 
                    <>{myObj.tags.join(", ")}</>
                  :
                    <StringsBox
                      name="Tags"
                      values={myObj.tags}
                      disabled={disabled}
                      onChange={(changed: string[]) => {
                        const new_obj = myObj;
                        new_obj.tags = changed;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                        onChange(new_obj);
                      }}
                    />
                  }
                </Grid>
                { !disabled && 
                  <>
                    {/* { renderColors() } */}
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <span className="header padded">
                        Limitations
                      </span>
                      <AddButton
                        onClick={ () => {
                          const new_obj = myObj;
                          new_obj.limitations.push(new CharacterLimitation());
                          setObj(new_obj);
                          setChangeTime(common_util.get_time_number());
                          onChange(new_obj);
                        }}
                      />
                    </Grid>
                    { myObj.limitations.map(limit => {
                      return (
                        <Grid
                          key={limit.true_id}
                          size={{
                            xs: 12
                          }}>
                          <CharacterLimitationBox
                            obj={limit}
                            onChange={() => {
                              const new_obj = myObj;
                              setObj(new_obj);
                              setChangeTime(common_util.get_time_number());
                              onChange(new_obj);
                            }}
                            onDelete={() => {
                              const new_obj = myObj;
                              new_obj.limitations = new_obj.limitations.filter(o => o.true_id !== limit.true_id);
                              setObj(new_obj);
                              setChangeTime(common_util.get_time_number());
                              onChange(new_obj);
                            }}
                          />
                        </Grid>
                      );
                    })}
                    { children && 
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        { children }
                      </Grid>
                    }
                  </>
                }
              </Grid>
            </ModelBaseDetails>
          </Grid>
        </>
      );
    } else if (tab === "Features") {
      content = (
        <Grid
          size={{
            xs: 12
          }}>
          <FeatureBasesInput 
            feature_bases={myObj.feature_bases} 
            feature_source={myObj}
            // parent_id={myObj._id} 
            // parent_type="Race"
            disabled={disabled}
            onExpand={setExpandedFeatureBase}
            level_filter={level_filter}
            onLevelFilterChange={setLevelFilter}
            height={height}
            onAdd={() => {
              const new_obj = myObj;
              const feature_base = new FeatureBase();
              feature_base.level = level_filter > -1 ? level_filter : 1;
              feature_base.feature_source_id = new_obj._id;
              feature_base.feature_source_type = new_obj.feature_source_type;
              new_obj.feature_bases.push(feature_base);
              setObj(new_obj);
              setExpandedFeatureBase(feature_base);
              setChangeTime(common_util.get_time_number());
              onChange(myObj);
            }}
            onReorder={(updated) => {
              const new_obj = myObj;
              // for (const u of updated) {
              //   const fb = new_obj.feature_bases.find(o => o.true_id === u.true_id);
              //   if (fb) {
              //     fb.copy(u);
              //   }
              // }
              new_obj.feature_bases = updated;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
              onChange(myObj);
            }}
          />
        </Grid>
      );
    } else if (tab === "Optional Features") {
      if (obj._id === "") {
        content = (
          <Grid
            size={{
              xs: 12
            }}>
            Optional Features cannot be added on new objects. Save and then come back to be able to add these.
          </Grid>
        );
      } else {
        content = (
          <Grid
            size={{
              xs: 12
            }}>
            <OptionalFeatureBasesInput 
              optional_feature_bases={optional_feature_bases} 
              parent_id={myObj._id} 
              parent_type="Race"
              source_book_ids={myObj.source_book_ids}
              onExpand={setExpandedFeatureBase}
              
              onAdd={() => {
                if (api.user) {
                  const optional_feature_base = new OptionalFeatureBase();
                  if (api.user.admin) {
                    api.applyWorkingOn(optional_feature_base);
                  } else {
                    // optional_feature_base.feature_source_type = myObj.feature_source_type;
                    optional_feature_base.owner_id = api.user._id;

                    optional_feature_base.source_books_old = [{ type: "Homebrew", id: "Personal", third_party: false }];
                  }
                  optional_feature_base.feature_source_id = myObj._id;
                  const new_optional_feature_bases = [...optional_feature_bases];
                  new_optional_feature_bases.push(optional_feature_base);
                  setOptionalFeatureBases(new_optional_feature_bases);
                  setExpandedFeatureBase(optional_feature_base);
                  onChange(myObj);
                }
              }}
            />
          </Grid>
        );
      }
    } else if (tab === "Table") {
      return (
        <Grid container spacing={0} direction="row">
          { !disabled && obj.table.entries.length === 0 &&
            <>
              <Grid
                size={{
                  xs: 9
                }}>
                <SelectObjectBox  
                  name="Select Table"
                  data_type="table"
                  value={table_id}
                  onChange={setTableID}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <ButtonBox
                  name="Copy Table"
                  disabled={table_id === ""}
                  onClick={() => {
                    const table = api.smart_hash.table.find(o => o._id === table_id);
                    if (table instanceof Table) {
                      const new_obj = obj;
                      new_obj.table.copyBase(table);
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
            </>
          }
          <Grid
            size={{
              xs: 12
            }}>
            <TableInput
              obj={obj.table}
              disabled={disabled}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.table.copyBase(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      ); 
    } else if (Object.keys(extra_menu_options).includes(tab)) {
      content = extra_menu_options[tab];
    }
    return (
      <Grid
        size={{
          xs: 12
        }}>
        {/* <div style={{ height: "100%" }}> */}
        <Grid container spacing={0} direction="row" style={{ 
          maxHeight: "100%", 
          // overflowY: "auto", 
          width: "100%", 
          // overflowX: "hidden",
        }}>
          { content }
        </Grid>
        {/* </div> */}
      </Grid>
    );
  }

  const renderHeader = () => {
    if (header) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          { header }
        </Grid>
      );
    }
    return null;
  }

  const renderFooter = () => {
    if (footer) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          { footer }
        </Grid>
      );
    }
  }

  const changeTab = (newTab: string) => {
    setTab(newTab);
    if (onTabChange) {
      onTabChange(newTab);
    }
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || loading) {
    return (<LoadingBox />);
  } else if (disabled) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <ArrowBackButton
            fab
            onClick={ () => {
              setNavigateTo(`/${data_type}`);
            }}
          />
        </Grid>
        <Grid size={12}>
          <DisplayStringWithReplacementsRecursive display_string={`{feature_source_desc.${obj.data_type}.${obj._id}}`} cfs={obj} />
        </Grid>
      </Grid>
    );
  } else if (expanded_feature_base) {
    if (expanded_feature_base instanceof FeatureBase) {
      // console.log(myObj.table);
      return (
        <div className="padded" style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={myObj.name}
            feature_base={expanded_feature_base} 
            feature_bases={myObj.feature_bases}
            feature_source={myObj}
            disabled={disabled}
            attached_table={myObj.table}
            onChange={(changed: FeatureBase) => {
              console.log(changed);
              const new_obj = myObj;
              const objFinder = new_obj.feature_bases.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                onChange(myObj);
              }
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = myObj;
                const feature_bases = new_obj.feature_bases.filter(o => o.id !== id);
                new_obj.feature_bases = feature_bases;
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
                onChange(myObj);
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
              onChange(myObj);
            }}
          />
        </div>
      );
    } else {
      return (
        <div className="padded" style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <OptionalFeatureBaseInput
            parent_name={myObj.name}
            optional_feature_base={expanded_feature_base} 
            feature_bases={myObj.feature_bases}
            attached_table={myObj.table}
            // onChange={() => { }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                setOptionalFeatureBases(optional_feature_bases.filter(o => o.id !== id));
                setExpandedFeatureBase(null);
                onChange(myObj);
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
              onChange(myObj);
            }}
          />
        </div>
      );
    }
  } else { 
    const dt_info = DATA_TYPE_INFO.find(o => o.data_type === obj.data_type);
    // const height = window.innerHeight - api.topHeight;
    // console.log(height);
    // console.log(topHeight);
    const menu_options = disabled ? ["Description","Features"] : ["Description","Features","Optional Features","Table",...Object.keys(extra_menu_options)];
    return (
      <Grid container spacing={0} direction="row" className="padded">
        <Grid
          style={ topHeight === 0 ? {} : { height: topHeight } }
          size={12}>
          <Grid container spacing={0} direction="row" ref={heightFinder}>
            <Grid size={3}>
              <ArrowBackButton
                fab
                onClick={ () => {
                  setNavigateTo(`/${obj.data_type}`);
                }}
              />
            </Grid>
            { !disabled &&
              <Grid size={9}>
                <TemplateBox
                  obj={obj}
                  type={dt_info?.name??""}
                  useTemplate={(template: TemplateBase) => {
                    const new_obj = obj;
                    new_obj.copyTemplate(template);
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
            <Grid size={12}>
              <span className="header">
                { disabled ? obj.name : obj._id === "" ? `Create ${FEATURE_SOURCE_TYPE_TO_NAME_MAP[data_type]}` : `Edit ${obj.name}` }
              </span>
            </Grid>
            { renderHeader() }
            { !disabled &&
              <>
                <Grid size={12}>
                  <StringBox 
                    value={myObj.name} 
                    message={myObj.name.length > 0 ? "" : "Name Invalid"}
                    name="Name"
                    disabled={disabled}
                    onBlur={(changed: string) => {
                      const new_obj = myObj;
                      new_obj.name = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      onChange(myObj);
                    }}
                  />
                </Grid>
              </>
            }
            <Grid size={12} style={{ height: "23px" }}>
              <CenteredMenu 
                options={menu_options}
                selected={tab}
                onSelect={changeTab}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Grid container spacing={0} direction="row">
            { renderTab() }
          </Grid>
        </Grid>
        { renderFooter() }
      </Grid>
    ); 
  }
}